import { translateUi } from "@i18n/config";
export type ParsedApiError = {
  message: string;
  code?: string;
  status?: number;
  correlationId?: string;
  detail?: string;
  retryable?: boolean;
  errors?: Record<string, string[]>;
  slugConflict?: ProductSlugConflictPayload;
};

export type ProductSlugConflictPayload = {
  type?: string;
  slug?: string;
  existingProductId?: string;
  isSameProduct?: boolean;
  source?: string;
};

type ErrorPayload = {
  message?: string;
  error?: string;
  detail?: string;
  title?: string;
  code?: string;
  errorCode?: string;
  status?: number;
  statusCode?: number;
  correlationId?: string;
  correlationID?: string;
  traceId?: string;
  requestId?: string;
  retryable?: boolean;
  errors?: Record<string, unknown>;
  fieldErrors?: Record<string, unknown>;
  validationErrors?: Record<string, unknown>;
  slugConflict?: ProductSlugConflictPayload;
};

function normalizeValidationErrors(
  errors?: unknown,
): Record<string, string[]> | undefined {
  if (!errors || typeof errors !== "object") return undefined;
  const entries = Object.entries(errors as Record<string, unknown>).filter(
    ([key]) => Boolean(key),
  );
  if (!entries.length) return undefined;

  const normalized = entries.reduce<Record<string, string[]>>(
    (acc, [field, value]) => {
      const messages = Array.isArray(value)
        ? value
            .map((item) =>
              typeof item === "string"
                ? item
                : item == null
                  ? ""
                  : String(item),
            )
            .map((item) => item.trim())
            .filter(Boolean)
        : typeof value === "string"
          ? [value.trim()].filter(Boolean)
          : value && typeof value === "object" && "message" in value
            ? [
                String((value as { message?: unknown }).message ?? "").trim(),
              ].filter(Boolean)
            : [String(value ?? "").trim()].filter(Boolean);

      if (messages.length > 0) {
        acc[field] = messages;
      }
      return acc;
    },
    {},
  );

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function summarizeValidationErrors(
  errors?: Record<string, string[]>,
): string | undefined {
  if (!errors || typeof errors !== "object") return undefined;
  const entries = Object.entries(errors).filter(([key]) => Boolean(key));
  if (!entries.length) return undefined;
  const parts = entries.slice(0, 3).map(([field, value]) => {
    return translateUi("value0Value1InterpolatedColonSpaced", "{{value0}}: {{value1}}", {
      value0: field,
      value1: value[0] ?? "Invalid value",
    });
  });
  return parts.join("; ");
}

function parsePayload(data: ErrorPayload | undefined) {
  if (!data) return {};
  const detailsValue: any = (data as any).details;
  const errors =
    normalizeValidationErrors(data.errors) ??
    normalizeValidationErrors(data.fieldErrors) ??
    normalizeValidationErrors(data.validationErrors) ??
    normalizeValidationErrors(detailsValue?.errors) ??
    normalizeValidationErrors(detailsValue?.fieldErrors) ??
    normalizeValidationErrors(detailsValue?.validationErrors);
  const validation = summarizeValidationErrors(errors);
  const detailsMessage =
    typeof detailsValue === "string"
      ? detailsValue
      : detailsValue && typeof detailsValue === "object"
        ? detailsValue.message ||
          detailsValue.error ||
          detailsValue.detail ||
          detailsValue.title
        : undefined;
  const message =
    data.message ||
    data.error ||
    data.detail ||
    data.title ||
    detailsMessage ||
    validation;
  const detail =
    validation && message !== validation
      ? validation
      : data.detail || detailsMessage;
  const code = data.code || data.errorCode;
  const correlationId =
    data.correlationId ||
    data.correlationID ||
    data.traceId ||
    data.requestId ||
    (detailsValue && typeof detailsValue === "object"
      ? detailsValue.correlationId || detailsValue.correlationID
      : undefined);
  const status =
    typeof data.status === "number"
      ? data.status
      : typeof data.statusCode === "number"
        ? data.statusCode
        : undefined;
  const retryable =
    typeof data.retryable === "boolean"
      ? data.retryable
      : detailsValue &&
          typeof detailsValue === "object" &&
          typeof detailsValue.retryable === "boolean"
        ? detailsValue.retryable
        : undefined;
  return {
    message,
    detail,
    code,
    correlationId,
    status,
    errors,
    retryable,
    slugConflict: data.slugConflict,
  };
}

function hasPayloadValues(payload: Record<string, unknown>) {
  return Object.values(payload).some(
    (value) => value !== undefined && value !== null && value !== "",
  );
}

function parseBody(body: unknown): ErrorPayload | undefined {
  if (!body) return undefined;
  if (typeof body === "string") {
    try {
      return JSON.parse(body) as ErrorPayload;
    } catch {
      return { message: body };
    }
  }
  if (typeof body === "object") return body as ErrorPayload;
  return { message: String(body) };
}

function isGenericMessage(message: string | undefined) {
  if (!message) return true;
  const normalized = message.trim().toLowerCase();
  if (/^http\s+\d+/.test(normalized)) return true;
  if (
    normalized === "network error" ||
    normalized === "network error during upload"
  )
    return true;
  if (
    normalized === "validation failed" ||
    normalized === "one or more validation errors occurred" ||
    normalized === "one or more validation errors occurred."
  )
    return true;
  if (normalized === "unauthorized") return true;
  if (normalized === "failed to save" || normalized === "failed to save.")
    return true;
  if (
    normalized === "upload failed" ||
    normalized === "upload failed." ||
    normalized === "upload failed. please retry." ||
    normalized === "upload failed. please retry"
  )
    return true;
  if (normalized === "media upload failed") return true;
  if (normalized.startsWith("upload failed with status")) return true;
  if (normalized.startsWith("request failed with status")) return true;
  if (
    normalized === "failed to fetch" ||
    normalized === "load failed" ||
    normalized === "failed to request"
  )
    return true;
  if (
    normalized.includes("connection lost") ||
    normalized.includes("no internet")
  )
    return true;
  if (normalized === "invalid response format") return true;

  return false;
}

const sensitivePatterns: RegExp[] = [
  /\b(bucket|s3|amazon\s*s3|aws\b|access\s*key|secret|credential|connection\s*string|endpoint|region|account\s*id|arn:|minio|azure\s*blob|google\s*cloud\s*storage|gcs)\b/i,
  /\b(AWS_ACCESS_KEY_ID|AWS_SECRET_ACCESS_KEY|AZURE_STORAGE|GOOGLE_APPLICATION_CREDENTIALS)\b/i,
  /\b\w*Exception\b/,
  /\bTraceback\s+\(most\s+recent\s+call\s+last\)/i,
  /\b(stack\s*trace|exception|inner\s*exception)\b/i,
  /\b(System\.|Microsoft\.|Amazon\.|Azure\.|Google\.)\w+/,
  /\bat\s+[A-Za-z0-9_.]+\s*\(.+\)/,
  /\bat\s+.+:\s*line\s+\d+/i,
  /^\s*<!doctype\s+html/i,
  /<html[\s>]/i,
  /\bhttps?:\/\/\S+/i,
];

export function isSensitiveMessage(
  message: string | undefined | null,
): boolean {
  if (!message) return false;
  const trimmed = message.trim();
  if (!trimmed) return false;
  if (trimmed.length > 2000) return true;
  return sensitivePatterns.some((pattern) => pattern.test(trimmed));
}

function mapStatusMessage(status?: number) {
  if (!status) return undefined;
  if (status === 400)
    return translateUi(
      "pleaseCheckTheHighlightedFieldsAndTryAgain",
      "Please check the highlighted fields and try again.",
    );
  if (status === 401)
    return translateUi(
      "yourSessionHasExpiredPleaseSignInAgain",
      "Your session has expired. Please sign in again.",
    );
  if (status === 403)
    return translateUi(
      "accessDeniedYouDoNotHavePermissionTo",
      "Access denied. You do not have permission to perform this action.",
    );
  if (status === 404)
    return translateUi(
      "notFoundTheRequestedResourceCouldNotBe",
      "Not found. The requested resource could not be found.",
    );
  if (status === 408)
    return translateUi(
      "requestTimedOutCheckYourConnectionAndTry",
      "Request timed out. Check your connection and try again.",
    );
  if (status === 409)
    return translateUi(
      "thisConflictsWithAnExistingItemOrA",
      "This conflicts with an existing item or a newer saved version. Review and try again.",
    );
  if (status === 413)
    return translateUi(
      "theFileIsTooLargeMax64Mb",
      "The file is too large (max 64MB). Please upload a smaller file.",
    );
  if (status === 429)
    return translateUi(
      "tooManyRequestsPleaseWaitAndTryAgain",
      "Too many requests. Please wait and try again.",
    );
  if (status >= 500)
    return translateUi(
      "serverErrorPleaseTryAgainInAMoment",
      "Server error. Please try again in a moment.",
    );
  if (status >= 400)
    return translateUi(
      "pleaseCheckYourInputAndTryAgain",
      "Please check your input and try again.",
    );
  return undefined;
}

function mapCodeMessage(code?: string) {
  if (!code) return undefined;
  const normalized = code.trim().toUpperCase();
  switch (normalized) {
    case "FILE_REQUIRED":
      return translateUi(
        "pleaseChooseAFileToUpload",
        "Please choose a file to upload.",
      );
    case "FILE_TOO_LARGE":
      return translateUi(
        "theFileIsTooLargeMax64Mb",
        "The file is too large (max 64MB). Please upload a smaller file.",
      );
    case "INVALID_FILE_TYPE":
      return translateUi(
        "invalidFileTypeUploadAPngJpgJpeg",
        "Invalid file type. Upload a PNG, JPG/JPEG, or WebP image.",
      );
    case "TENANT_MISSING":
      return translateUi(
        "weCouldNotDetermineYourStoreContextRefresh",
        "We could not determine your store context. Refresh the page and try again.",
      );
    case "STORAGE_CONFIG":
      return translateUi("uploadFailedPleaseRetry", "Upload failed. Please retry.");
    case "STORAGE_ACCESS":
      return translateUi("uploadFailedPleaseRetry", "Upload failed. Please retry.");
    case "STORAGE_UNAVAILABLE":
      return translateUi("uploadFailedPleaseRetry", "Upload failed. Please retry.");
    case "UPLOAD_INVALID":
      return translateUi(
        "theUploadRequestIsInvalidPleaseCheckThe",
        "The upload request is invalid. Please check the file and try again.",
      );
    case "UPLOAD_FAILED":
      return translateUi("uploadFailedPleaseRetry", "Upload failed. Please retry.");
    case "NETWORK_ERROR":
      return translateUi(
        "connectionLostCheckYourConnectionAndTryAgain",
        "Connection lost. Check your connection and try again.",
      );
    case "REQUEST_TIMEOUT":
      return translateUi(
        "requestTimedOutCheckYourConnectionAndTry",
        "Request timed out. Check your connection and try again.",
      );
    default:
      return undefined;
  }
}

function shouldPreferStatusMessage(status?: number) {
  return (
    status === 401 ||
    status === 403 ||
    (typeof status === "number" && status >= 500)
  );
}

function shouldUseRawMessage(message: string | undefined, status?: number) {
  if (!message) return false;
  if (shouldPreferStatusMessage(status)) return false;
  return !isGenericMessage(message) && !isSensitiveMessage(message);
}

function hasValidationErrors(errors?: Record<string, string[]>): boolean {
  return Boolean(errors && Object.keys(errors).length > 0);
}

function shouldAppendCodeToMessage(code?: string): boolean {
  if (!code) return false;
  const trimmed = code.trim();
  if (!trimmed) return false;
  if (trimmed.toLowerCase() === "validation_failed") return false;
  return /^[A-Z0-9_]+$/.test(trimmed) && trimmed.includes("_");
}

export function parseApiError(
  err: unknown,
  fallbackMessage = translateUi(
    "somethingWentWrongPleaseTryAgain",
    "Something went wrong. Please try again.",
  ),
): ParsedApiError {
  if (!err) return { message: fallbackMessage };

  const maybeError = err as any;
  const status =
    (typeof maybeError?.status === "number" ? maybeError.status : undefined) ??
    (typeof maybeError?.response?.status === "number"
      ? maybeError.response.status
      : undefined);

  const payloadFromResponse = parsePayload(
    maybeError?.response?.data as ErrorPayload | undefined,
  );
  const payloadFromBody = parsePayload(parseBody(maybeError?.body));
  const payload = hasPayloadValues(payloadFromResponse)
    ? payloadFromResponse
    : hasPayloadValues(payloadFromBody)
      ? payloadFromBody
      : undefined;
  const directErrors =
    normalizeValidationErrors(maybeError?.errors) ??
    normalizeValidationErrors(maybeError?.fieldErrors) ??
    normalizeValidationErrors(maybeError?.validationErrors);
  const validationErrors =
    (payloadFromResponse?.errors as Record<string, string[]> | undefined) ??
    (payloadFromBody?.errors as Record<string, string[]> | undefined) ??
    directErrors;

  const payloadMessage = payload?.message;
  const rawMessage =
    payloadMessage ||
    (typeof maybeError?.message === "string" ? maybeError.message : undefined);
  const fallbackCode =
    typeof maybeError?.code === "string" ? maybeError.code : undefined;
  const fallbackDetail =
    typeof maybeError?.detail === "string" ? maybeError.detail : undefined;
  const fallbackCorrelation =
    typeof maybeError?.correlationId === "string"
      ? maybeError.correlationId
      : typeof maybeError?.correlationID === "string"
        ? maybeError.correlationID
        : typeof maybeError?.requestId === "string"
          ? maybeError.requestId
          : undefined;
  const fallbackRetryable =
    typeof maybeError?.retryable === "boolean"
      ? maybeError.retryable
      : undefined;
  const code = payload?.code ?? fallbackCode;
  const finalStatus = status || payload?.status;
  const friendlyFromStatus = mapStatusMessage(finalStatus);
  const friendlyFromCode = mapCodeMessage(code);
  const validationSummary = summarizeValidationErrors(validationErrors);
  const isValidationFailure =
    finalStatus === 400 && hasValidationErrors(validationErrors);
  const isNetworkFailure =
    !finalStatus ||
    finalStatus === 0 ||
    finalStatus === 408 ||
    code === "NETWORK_ERROR" ||
    code === "REQUEST_TIMEOUT";
  let message: string;
  if (shouldUseRawMessage(rawMessage, finalStatus)) {
    message = rawMessage as string;
  } else {
    message =
      friendlyFromCode ||
      (isValidationFailure
        ? "Please check the highlighted fields and try again."
        : undefined) ||
      (finalStatus === 400 ? validationSummary : undefined) ||
      friendlyFromStatus ||
      (isNetworkFailure
        ? translateUi(
            "connectionLostCheckYourConnectionAndTryAgain",
            "Connection lost. Check your connection and try again.",
          )
        : fallbackMessage);
  }

  // Append application codes only. HTTP status and correlation IDs remain in
  // structured details so user-facing copy stays clean.
  if (shouldAppendCodeToMessage(code) && !message.includes(code as string)) {
    message = `${message} (${code})`;
  }

  const detailCandidate =
    payload?.detail ??
    fallbackDetail ??
    (rawMessage && rawMessage !== message ? rawMessage : undefined);
  const detail =
    detailCandidate &&
    finalStatus &&
    finalStatus < 500 &&
    !isSensitiveMessage(detailCandidate)
      ? detailCandidate
      : undefined;
  const correlationId = payload?.correlationId ?? fallbackCorrelation;
  const retryable =
    typeof payload?.retryable === "boolean"
      ? payload.retryable
      : typeof fallbackRetryable === "boolean"
        ? fallbackRetryable
        : finalStatus
          ? finalStatus >= 500 || finalStatus === 408 || finalStatus === 429
          : isNetworkFailure || undefined;

  return {
    message,
    code,
    status: finalStatus,
    correlationId,
    detail,
    retryable,
    errors: validationErrors,
    slugConflict: payload?.slugConflict,
  };
}

export function isUnauthorizedError(err: unknown): boolean {
  if (!err) return false;

  const maybeError = err as any;
  const status =
    (typeof maybeError?.status === "number" ? maybeError.status : undefined) ??
    (typeof maybeError?.statusCode === "number"
      ? maybeError.statusCode
      : undefined) ??
    (typeof maybeError?.response?.status === "number"
      ? maybeError.response.status
      : undefined);

  if (status === 401) {
    return true;
  }

  const code =
    typeof maybeError?.code === "string"
      ? maybeError.code.trim().toUpperCase()
      : "";
  if (code === "AUTH_UNAUTHORIZED") {
    return true;
  }

  const message =
    typeof maybeError?.message === "string"
      ? maybeError.message.trim().toLowerCase()
      : "";
  return message === "unauthorized" || message.includes("session expired");
}

export function formatErrorDetails(
  info?: ParsedApiError | null,
): string | null {
  if (!info) return null;
  const lines: string[] = [];
  if (info.code)
    lines.push(
      translateUi("codeValue0", "Code: {{value0}}", {
        value0: info.code,
      }),
    );
  if (typeof info.status === "number")
    lines.push(
      translateUi("statusValue0TitleCaseColonInterpolated", "Status: {{value0}}", {
        value0: info.status,
      }),
    );
  if (info.correlationId)
    lines.push(
      translateUi("correlationIdValue0", "Correlation ID: {{value0}}", {
        value0: info.correlationId,
      }),
    );
  if (info.detail && !isSensitiveMessage(info.detail))
    lines.push(
      translateUi("detailValue0", "Detail: {{value0}}", {
        value0: info.detail,
      }),
    );
  if (!lines.length) return null;
  return lines.join("\n");
}
